import { createRouter, createWebHistory } from 'vue-router';
import HomeLayout from '../layout/HomeLayout.vue'
import HomePage from '@pages/Homepage.vue'
import { useAuthUserStore}  from '@stores/auth';
import { Hub } from "@aws-amplify/core"
import { ConfigService } from '@/services/ConfigService';
import { notify } from '../components/NotificationPlugin';
import { useSubtitleStore } from '@stores/subtitleStore';
import { useDashboardStore } from '@stores/dashboardStore';
import { stopWebsocket } from '../services/WebsocketService';
import AutoService from '@services/AutoService';
import { getPostLoginPath } from '@utils/postLoginRedirect';
import { authReady } from '@utils/authReady';
import { getTranslation } from '../i18nlocal';

// Pas 1.1: every page except the landing (HomeLayout + Homepage, the
// LCP path) is a lazy chunk. DashboardLayout included — it only ever
// renders behind auth.
const DashboardLayout = () => import('../layout/DashboardLayout.vue');

Hub.listen("auth", async (data) => {
    const authStore = useAuthUserStore();
    const subtitleStore = useSubtitleStore();
    const dashboardStore = useDashboardStore();

    const { event, data: payload } = data.payload;

    switch (data.payload.event) {
      case 'tokenRefresh':
        //console.log('Token refresh event:', JSON.stringify(data, null, 2));
        break;
      case 'signedOut':
        // Properly use Pinia store
        authStore.clearUser();
        // subtitleStore.clearCache() rejects on IndexedDB failure
        // (TranscriptAdoptionError 'conflict'/'unavailable') after showing its
        // own user-facing toast. Observe the rejection so it is never
        // unhandled, but never let a failed wipe block sign-out.
        try {
          Promise.resolve(subtitleStore.clearCache()).catch((error) => {
            console.error('Sign-out: failed to clear local transcript cache:', error);
          });
        } catch (error) {
          console.error('Sign-out: failed to clear local transcript cache:', error);
        }
        dashboardStore.clearCache();
        stopWebsocket();
        router.push('/login');
        break;
      case 'signedIn':
        try {
          const user = await authStore.getUser();
          
          if (user) {
            // Get consents from localStorage. A malformed stored value must
            // never skip the post-login redirect below — parse it in its own
            // try/catch and treat unparsable data as no saved consents.
            const savedConsents = localStorage.getItem('userConsents');
            let consentsData = null;
            if (savedConsents) {
              try {
                consentsData = JSON.parse(savedConsents);
              } catch {
                consentsData = null;
              }
            }
            if (consentsData) {
              // Extract just the consent values (removing uploadedToServer flag)
              const { uploadedToServer, userId, ...consentValues } = consentsData;
              if (!uploadedToServer || userId != user.username) {
                try {
                  // Extract consent-specific data and metadata
                  const { termsAndConditions, privacyPolicy, functional, analytics, 
                          dataProcessing, dataTransfer, marketing } = consentValues;
                  let userId = user.attributes.sub || user.username;
                  await AutoService.fetchOrder('users', userId, {
                    consents: {
                      termsAndConditions,
                      privacyPolicy,
                      functional,
                      analytics,
                      dataProcessing,
                      dataTransfer,
                      marketing
                    },
                    timestamp: consentValues.timestamp,
                    version: consentValues.version
                  });
          
                  // Mark as uploaded in localStorage while preserving all other data
                  localStorage.setItem('userConsents', JSON.stringify({
                    ...consentValues,
                    userId: userId,
                    uploadedToServer: true
                  }));
                } catch (error) {
                  console.error('Failed to record consents:', error);
                }
              }
            }
          }
          // Role-aware landing: admins go to their console, users to the
          // customer dashboard. The marketing homepage stays at /home.
          router.push(getPostLoginPath(authStore.userGroups));
        } catch (error) {
          console.error('Error getting user after sign in:', error);
        }
        break;
  
      // Add handling for social sign-in event from Identity Pool auth
      case 'socialSignIn':
        try {
          // This event is dispatched by our modified googleSignIn function
          // Attempt to get the user through standard methods
          const userData = await authStore.getUser();
          
          if (!userData) {
            await authStore.getIdentityPoolUser();
          }
          
          router.push('/');
        } catch (error) {
          console.error('Error handling socialSignIn event:', error);
        }
        break;
      case 'cognitoHostedUI_failure':
        // payload?.data may contain auth details — do not log it.
        console.error('Social sign-in failed');
        router.push('/login?error=social_auth_failed');
        break;
      case "signInWithRedirect_failure":
        await authStore.logout();
        break;
      case "signInWithRedirect":
        try {
          // userData carries the Cognito session — never log it.
          await authStore.getUser();
        } catch (error) {
          console.error('Error getting user after redirect:', error);
        }
        break;
      case 'customOAuthState': {
        // payload?.data is the customState provided on signInWithRedirect — no action needed
        break;
      }
    }
});

const baseURL = import.meta.env.VITE_AMAZON_API || '';

// Dynamic import for authorized components
const dynamicImport = (componentName) => {
  // Every entry must be reachable via dynamicImport('<name>') below. Removed
  // dead entries: AdminDashboard/ReviewDashboard.vue are superseded by
  // AdminOverview (files kept, unrouted); ApiDocs has a direct-import route.
  const componentImportMap = {
    AdminOverview: () => import('@protected/AdminOverview.vue'),
    Orders: () => import('@protected/Orders.vue'),
    UserProfile: () => import('@protected/Profile.vue'),
    TeamManager: () => import('@protected/TeamManager.vue'),
    CorrectorDashboard: () => import('@protected/CorrectorDashboard.vue'),
    TranscriptorDashboard: () => import('@protected/TranscriptorDashboard.vue'),
    MainDashboard: () => import('@protected/MainDashboard.vue'),
    VideoGeneratorDashboard : () => import('@protected/VideoGeneratorDashboard.vue'),
    ModelsManager: () => import('@protected/ModelsManager.vue'),
    UsersManager: () => import('@protected/UsersManager.vue'),
    SubscriptionsManager: () => import('@protected/SubscriptionsManager.vue'),
    AssetsBrowser: () => import('@protected/AssetsBrowser.vue')
  };

  const importFunction = componentImportMap[componentName];

  if (importFunction) {
    // Call the import function to trigger the dynamic import and return the promise
    return importFunction();
  } else {
    // Handle the case where componentName is not found in the map
    return Promise.reject(new Error(`Component '${componentName}' not found`));
  }
};

const routes =  
[
  {
    path: '/',
    name: 'Root',
    component: HomeLayout,
    meta: { requires: "" },
    redirect: '/home',
    children: [
      {
        path: 'home',
        name: 'Home',
        component: HomePage
      },
      {
        path: 'login',
        name: 'Login',
        displayname: 'Login',
        component: () => import('@pages/Login.vue')
      },
      {
        path: 'signin',
        name: 'SignInCallback',
        component: () => import('@pages/auth/SignInCallback.vue'),
        meta: {
          title: 'Signing In...',
        }
      },
      {
        path: 'signout',
        name: 'SignOutCallback',
        component: () => import('@pages/auth/SignOutCallback.vue'),
        meta: {
          title: 'Signing Out...',
        }
      },
      {
        path: 'about',
        name: 'About',
        displayname: 'About',
        component: () => import('@pages/About.vue')
      },
      {
        // Pas 4.1: Help is now the Docs/Ghiduri section; the old path (and
        // route name, used by Navigation) redirects to the guides index.
        path: 'help',
        name: 'Help',
        displayname: 'Help',
        redirect: '/docs'
      },
      {
        path: 'docs',
        name: 'Docs',
        displayname: 'Guides',
        component: () => import('@pages/Docs.vue'),
        meta: { breadcrumb: 'Guides' }
      },
      {
        path: 'docs/:slug',
        name: 'DocGuide',
        displayname: 'Guide',
        component: () => import('@pages/Docs.vue'),
        props: true,
        meta: { breadcrumb: 'Guides' }
      },
      {
        path: 'terms',
        name: 'TermsAndConditions',
        displayname: 'Terms',
        component: () => import('@pages/Terms.vue')
      },
      {
        path: 'privacy',
        name: 'PrivacyPolicy',
        displayname: 'Privacy',
        component: () => import('@pages/Privacy.vue')
      },
      {
        path: 'data-handling',
        name: 'DataHandling',
        displayname: 'Data Handling',
        component: () => import('@pages/DataHandling.vue')
      },
      {
        path: 'upgrade',
        name: 'Upgrade',
        displayname: 'Upgrade',
        component: () => import('@/pages/Upgrade.vue'),
        meta: { breadcrumb: 'Upgrade' }
      }
    ]
  },
  {
    path: '/Users',
    meta: { requires: "Users" },
    component: DashboardLayout,
    name: 'UsersRoot',
    displayname: 'User Dashboard',
    redirect: '/Users/dashboard',
    beforeEnter(to, from, next) {
      const routeMeta = to.meta.requires;
      if (useAuthUserStore().isAuthorized(routeMeta)) {
          next();
      } else {
        next({ name: 'Unauthorized' });
      }
    },
    children: [
      {
        path: 'dashboard',
        name: 'UserDashboard',
        displayname: 'Dashboard',
        component: () => dynamicImport('MainDashboard').then((module) => module.default),
      },
      {
        path: 'transcriptordashboard',
        name: 'TranscriptEditorDashboard',
        displayname: 'Transcript Editor Dashboard',
        component: () => dynamicImport('TranscriptorDashboard').then((module) => module.default),
        meta: { breadcrumb: 'Editor' }
      },
      {
        path: 'transcriptordashboard/:projectId?/:inputId?/:elementId?',
        name: 'TranscriptEditorDashboardDeepLink',
        displayname: 'Transcript Editor Dashboard',
        component: () => dynamicImport('TranscriptorDashboard').then((module) => module.default),
        props: true,
        meta: { breadcrumb: 'Editor' }
      },
      {
        path: 'videodashboard',
        name: 'VideoGeneratorDashboard',
        displayname: 'Video Generator Dashboard',
        component: () => dynamicImport('VideoGeneratorDashboard').then((module) => module.default),
      },
      {
        path : 'profile',
        name: 'UserProfile',
        displayname: 'Profile',
        component: () => dynamicImport('UserProfile').then((module) => module.default),
      },
      {
        path: 'orders',
        name: 'UsersOrders',
        displayname: 'Orders',
        component: () => dynamicImport('Orders').then((module) => module.default),
        props: true,
      },
      {
        path: 'team',
        name: 'TeamManager',
        displayname: 'Team',
        component: () => dynamicImport('TeamManager').then((module) => module.default),
      },
      {
        path: 'transcripts', name: 'SavedTranscripts', displayname: 'Saved transcripts',
        component: () => import('@protected/SavedTranscripts.vue'),
      },
      {
        path: 'assets',
        name: 'AssetsBrowser',
        displayname: 'Assets',
        component: () => dynamicImport('AssetsBrowser').then((module) => module.default),
      }
    ]
  },
  { path: '/share/:shareId', name: 'SharedContent', component: () => import('@pages/SharedContent.vue') },
  {
    path: '/Correctors',
    meta: { requires: "Correctors", breadcrumb: 'Correctors' },
    component: DashboardLayout,
    displayname: 'Correctors',
    redirect: '/Correctors/dashboard',
    beforeEnter(to, from, next) {
      const routeMeta = to.meta.requires;
     
      if (useAuthUserStore().isAuthorized(routeMeta)) {
          next();
      } else {
        next({ name: 'Unauthorized' });
      }
    },
    children: [
      {
        path: 'dashboard',
        name: 'CorrectorDashboard',
        displayname: 'Corrector Dashboard',
        component: () => dynamicImport('CorrectorDashboard').then((module) => module.default),
      },
      {
        path : 'profile',
        name: 'CorrectorProfile',
        displayname: 'Profile',
        component: () => dynamicImport('UserProfile').then((module) => module.default),
      }
    ]
  },
  {
    path: '/SuperAdmins',
    meta: { requires: "SuperAdmins" },
    component: DashboardLayout,
    name: 'SuperAdminsRoot',
    displayname: 'Super Admins',
    redirect: { name: 'SuperAdminDashboard' },
    beforeEnter(to, from, next) {
      const routeMeta = to.meta.requires;
     
      if (useAuthUserStore().isAuthorized(routeMeta)) {
          next();
      } else {
        next({ name: 'Unauthorized' });
      }
    },
    children: [
      {
        path: 'dashboard',
        name: 'SuperAdminDashboard',
        displayname: 'Admin Dashboard',
        component: () => dynamicImport('AdminOverview').then((module) => module.default),
      },
      {
        path: 'usersmanager',
        name: 'UsersManager',
        displayname: 'Users Manager',
        component: () => dynamicImport('UsersManager').then((module) => module.default),
      },
      {
        path: 'modelsmanager',
        name: 'ModelsManager',
        displayname: 'Models Manager',
        component: () => dynamicImport('ModelsManager').then((module) => module.default),
      },
      {
        path: 'subscriptionsmanager',
        name: 'SubscriptionsManager',
        displayname: 'Subscriptions Manager',
        component: () => dynamicImport('SubscriptionsManager').then((module) => module.default),
      }
    ]
  },
  {
    path: '/Admins',
    meta: { requires: "Admins" },
    component: DashboardLayout,
    name: 'AdminsRoot',
    displayname: 'Admins',
    redirect: '/Admins/dashboard',
    beforeEnter(to, from, next) {
      const routeMeta = to.meta.requires;
     
      if (useAuthUserStore().isAuthorized(routeMeta)) {
          next();
      } else {
        next({ name: 'Unauthorized' });
      }
    },
    children: [
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        displayname: 'Admin Dashboard',
        component: () => dynamicImport('AdminOverview').then((module) => module.default),
      },
      {
        path: 'usersmanager',
        name: 'UsersManager',
        displayname: 'Users Manager',
        component: () => dynamicImport('UsersManager').then((module) => module.default),
      }
    ]
  },
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: () => import('@pages/Unauthorized.vue'),
  },
  {
    path: '/api-docs',
    name: 'ApiDocs',
    component: () => import('@pages/ApiDocs.vue'),
    meta: { breadcrumb: 'API Docs' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@pages/NotFoundPage.vue'),
    meta: { breadcrumb: 'Not Found' }
  }
];

const router = createRouter({
  history:  createWebHistory('/'),
  routes,
  linkExactActiveClass: "active", // active class for *exact* links.
  linkActiveClass: "nav-item active",
  scrollBehavior: (to, from, savedPosition) => {
    if (to.hash) {
      return { selector: to.hash };
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach(async (to, from, next) => {
  try {
    // Pas 1.1: the app mounts before Amplify is configured (background
    // init in main.js). Public routes must not wait for it; protected
    // routes (meta.requires anywhere in the matched chain) still do,
    // so dashboard data fetches never race an unconfigured Auth.
    if (to.matched.some((r) => r.meta?.requires)) {
      await authReady;
    }
    let appConfig = await ConfigService.loadConfig(to.query?.customerId, null);
    document.title = appConfig?.name || 'Transcriptor'; // replace 'Default Title' with your default title
    next();
  } catch (error) {
    console.error(error);
    notify('danger', getTranslation('errors.configLoad'));
    next(false);
  }
});

router.afterEach((to) => {
  try {
    import('@utils/analytics').then(({ trackPageView }) => trackPageView(to.fullPath)).catch((err) => { void err; });
  } catch { /* analytics must never break routing */ }
});

export default router;
